import { useEffect, useMemo, useState } from "react";
import "../App.css";
import { auth, db } from "./components/firebaseIniti.ts";
import { onAuthStateChanged, signInWithEmailAndPassword } from "firebase/auth";
import firestorehandler from "./components/firestorehandler.ts";
import { ShoppingCartModel, ShoppingCartProductModel } from "./components/firestoreData.ts";
import CreateExcel from "./components/excel.ts";
import CreateExcelForSymbrio from "./components/excelForSymbio.ts";

function App() {
  const [orders, setOrders] = useState<ShoppingCartProductModel[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  // Responsive items per page (kept)
  const itemsPerPage = useMemo(() => {
    if (typeof window === "undefined") return 12;
    const w = window.innerWidth;
    if (w < 400) return 4;     // very small phones
    if (w < 640) return 6;     // phones
    if (w < 1024) return 9;    // tablets
    return 12;                 // desktop
  }, []);

  const firebaseEmail = import.meta.env.VITE_FIREBASE_EMAIL;
  const firebasePassword = import.meta.env.VITE_FIREBASE_PASSWORD;

  async function autoLogin() {
    try {
      if (auth.currentUser) return;
      await signInWithEmailAndPassword(auth, firebaseEmail, firebasePassword);
    } catch (error) {
      console.error("Auto-login failed:", error);
    }
  }

  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async function deleteAllItems() {
    const handler = new firestorehandler(db, "0jAREtpDQNVm3c49T28HaxC3LGe2");
    await handler.DeleteAllProjectProductInShoppingCart("6BZdug56A3I8jnUtbdUo");
    await handler.DeleteAllProjectProductInShoppingCart("8PM5AdICDE2Bn1xHRpIi");
    await handler.DeleteAllProjectProductInShoppingCart("9wQwUGIE7a9rqTfuDbgr");
    await handler.DeleteAllProjectProductInShoppingCart("C9tzDPLYaH7WxGf4wjV8");
    await handler.DeleteAllProjectProductInShoppingCart("LRlujBbsR16layKN0pYd");
    await handler.DeleteAllProjectProductInShoppingCart("WKz6NqNYk9OSclFGF2EX");
    await handler.DeleteAllProjectProductInShoppingCart("bGnJuKQzUuztJwHaZSrx");
    setOrders([]);
  }

  async function getData(userId: string): Promise<ShoppingCartModel | null> {
    if (!userId) return null;

    const handler = new firestorehandler(db, userId);
    const userData = await handler.GetUserInformation(userId);
    if (!userData) return null;

    const tempData = await handler.GetProductListFromShoppingCartByProject("348bgujbsdiu3g");
    if (!tempData || tempData.length === 0) return null;

    const mergedProducts = tempData.flatMap((cart) =>
      cart.products.map((product) => ({
        ...product,
        initiator: cart.initiator,
      }))
    );

    return {
      ...tempData[0],
      products: mergedProducts,
    };
  }

  useEffect(() => {
    autoLogin();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        getData(user.uid).then((data) => {
          if (!data?.products) return;
          // @ts-expect-error timestamp sort
          setOrders(data.products.sort((a, b) => b.timestamp - a.timestamp));
        });
      } else {
        setOrders([]);
      }
    });
    return () => unsubscribe();
  }, []);

  // Reset to first page when orders change or screen size bucket changes
  useEffect(() => {
    setCurrentPage(1);
  }, [orders, itemsPerPage]);

  const pressHandlerCreateExcel = () => CreateExcel(orders);
  const pressHandlerCreateSymbrioExcel = () => CreateExcelForSymbrio(orders);

  const pressHandlerDeleteAllItems = () => {
    const confirmDelete = window.confirm("Er du sikker på at du vil slette alle varene?");
    if (confirmDelete) deleteAllItems();
  };

  const totalPages = orders.length > 0 ? Math.ceil(orders.length / itemsPerPage) : 1;
  const paginatedOrders =
    orders.length > 0 ? orders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage) : [];

  return (
    <div className="app-container construction">
      {/* NOTE: keep meta+favicon in index.html */}

      <div className="topbar">
        {/* Title removed by request; subtitle centered via CSS */}
        <p className="app-subtitle">Husk å oppdatere side før bestilling!</p>
      </div>

      <div className="header-buttons" role="toolbar" aria-label="Actions">
        <button className="primary-button" onClick={pressHandlerCreateExcel}>
          📄 Lag ordreoversikt
        </button>
        <button className="primary-button" onClick={pressHandlerCreateSymbrioExcel}>
          📄 Lag Symbrio Import
        </button>
        <button className="danger-button" onClick={pressHandlerDeleteAllItems}>
          🗑️ Slett alle varer
        </button>
      </div>

      <div className="product-container">
        {paginatedOrders.length > 0 ? (
          paginatedOrders.map((order, index) => (
            <div key={index} className="product-card" role="article" aria-label={order.product.name}>
              <img
                loading="lazy"
                src={`/images/${order.product.image}.png`}
                alt={order.product.name}
                className="product-image"
              />
              <div className="product-info">
                <p className="product-name">{order.product.name}</p>
                <p className="product-article">
                  {order.product.article_number}, {order.product.dimension}
                </p>
                <p className="product-amount">
                  {order.amount} {order.product.measurement}
                </p>
              </div>
            </div>
          ))
        ) : (
          <p className="no-products">Ingen produkter er lagt til</p>
        )}
      </div>

      <div className="pagination" aria-label="Pagination">
        <button
          className="page-btn"
          disabled={currentPage === 1 || totalPages === 0}
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        >
          ◀ Forrige
        </button>
        <span className="page-status">
          Side {orders.length > 0 ? currentPage : 0} av {orders.length > 0 ? totalPages : 0}
        </span>
        <button
          className="page-btn"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        >
          Neste ▶
        </button>
      </div>
    </div>
  );
}

export default App;
